import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { clientAPI, invoiceAPI, quotationAPI, auditAPI } from '../../services';
import {
  LayoutGrid, Building2, UserMinus, Mail, ClipboardList, CircleDollarSign,
  Receipt, TrendingUp, BarChart3, Megaphone, History, LifeBuoy, Users,
  Settings, ShieldCheck, ChevronLeft, ChevronRight, Bell, Search, Menu, X,
  LogOut, KeyRound, FileText,
} from 'lucide-react';
import { C, FONT_UI, Mono } from '../Dashboard/superadmin/theme';

const BASE = '/dashboard';

/* Sidebar structure — edit here to add/remove menu items. `perm` is the
   platformPermissions key checked for the platform_staff role; items with
   no `perm` (Staff Management) stay superadmin-only. */
const NAV_GROUPS = [
  {
    label: 'General',
    dot: C.signal,
    items: [{ id: 'overview', path: BASE, label: 'Overview', icon: LayoutGrid, exact: true, always: true }],
  },
  {
    label: 'Clients',
    dot: C.signal,
    items: [
      { id: 'clients', path: `${BASE}/clients`, label: 'Clients', icon: Building2, perm: 'clients' },
      { id: 'separations', path: `${BASE}/separations`, label: 'Separations', icon: UserMinus, perm: 'separations' },
      { id: 'contact', path: `${BASE}/contact-submissions`, label: 'Contact Forms', icon: Mail, perm: 'contactForms' },
    ],
  },
  {
    label: 'Finance',
    dot: C.emerald,
    items: [
      { id: 'quotations', path: `${BASE}/quotations`, label: 'Quotations', icon: ClipboardList, badge: 'New', perm: 'quotations' },
      { id: 'billing', path: `${BASE}/billing`, label: 'Billing & Invoices', icon: CircleDollarSign, badge: 'New', perm: 'billing' },
      { id: 'expenditures', path: `${BASE}/expenditures`, label: 'Expenditures', icon: Receipt, perm: 'expenditures' },
      { id: 'profits', path: `${BASE}/profits`, label: 'Profits', icon: TrendingUp, perm: 'profits' },
    ],
  },
  {
    label: 'System',
    dot: C.violet,
    items: [
      { id: 'analytics', path: `${BASE}/analytics`, label: 'Analytics', icon: BarChart3, perm: 'analytics' },
      { id: 'announcements', path: `${BASE}/announcements`, label: 'Announcements', icon: Megaphone, badge: 'New', perm: 'announcements' },
      { id: 'audit', path: `${BASE}/audit-log`, label: 'Audit Log', icon: History, badge: 'New', perm: 'audit' },
      { id: 'support', path: `${BASE}/support`, label: 'Platform Support', icon: LifeBuoy, perm: 'platformSupport' },
      { id: 'staff', path: `${BASE}/staff`, label: 'Staff Management', icon: Users, superadminOnly: true },
      { id: 'settings', path: `${BASE}/settings`, label: 'Settings', icon: Settings, perm: 'settings' },
    ],
  },
];

function NavItem({ item, active, collapsed, onNavigate }) {
  const [hover, setHover] = useState(false);
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      title={collapsed ? item.label : ''}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative flex items-center gap-3 rounded-lg mx-2 transition-colors ${
        collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2'
      }`}
      style={{
        background: active ? 'rgba(255,255,255,0.09)' : hover ? 'rgba(255,255,255,0.05)' : 'transparent',
        color: active ? '#FFFFFF' : hover ? '#E2E6F5' : '#A8AEC9',
        fontFamily: FONT_UI,
      }}
    >
      {active && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r"
          style={{ width: 3, height: 18, background: C.signal }}
        />
      )}
      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="text-[13px] font-medium flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: C.amber, color: '#3A2600' }}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </Link>
  );
}

/* Global search — pulls the platform's clients, invoices, quotations and
   recent audit entries once on first focus, then filters them locally so
   typing stays instant and no new backend endpoint is required. */
function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const loadIndex = async () => {
    if (index || loading) return;
    setLoading(true);
    const safe = (p) => p.then((r) => r).catch(() => null);
    const [clients, invoices, quotes, logs] = await Promise.all([
      safe(clientAPI.getClients({ page: 1, limit: 100 })),
      safe(invoiceAPI.getInvoices({ limit: 100 })),
      safe(quotationAPI.getQuotations({ limit: 100 })),
      safe(auditAPI.getLogs({ limit: 50 })),
    ]);
    const rows = [];
    (clients?.data?.clients || []).forEach((c) =>
      rows.push({
        kind: 'Client', icon: Building2, label: c.name,
        sub: c.email || c.code || '', to: `${BASE}/clients`,
      })
    );
    (invoices?.data?.invoices || invoices?.data || []).forEach((i) =>
      rows.push({
        kind: 'Invoice', icon: CircleDollarSign,
        label: i.invoiceNumber || i.number || 'Invoice',
        sub: i.client?.name || i.clientName || '', to: `${BASE}/billing`,
      })
    );
    (quotes?.data?.quotations || quotes?.data || []).forEach((q) =>
      rows.push({
        kind: 'Quotation', icon: FileText,
        label: q.quotationNumber || q.number || 'Quotation',
        sub: q.organizationName || q.client?.name || '', to: `${BASE}/quotations`,
      })
    );
    (logs?.data?.logs || logs?.data || []).forEach((l) =>
      rows.push({
        kind: 'Log', icon: History,
        label: l.description || l.action || 'Activity',
        sub: l.module || '', to: `${BASE}/audit-log`,
      })
    );
    setIndex(rows);
    setLoading(false);
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !index) return [];
    return index
      .filter((r) => `${r.label} ${r.sub}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, index]);

  return (
    <div ref={boxRef} className="relative w-full max-w-[340px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.textFaint }} />
      <input
        value={query}
        onFocus={() => { setOpen(true); loadIndex(); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        placeholder="Search clients, invoices, logs..."
        className="w-full pl-9 pr-3 py-2 rounded-lg text-[13px] outline-none transition-colors focus:bg-white"
        style={{ background: '#F7F8FC', border: `1px solid ${C.border}`, color: C.ink }}
      />
      {open && query.trim() && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-xl overflow-hidden z-50"
          style={{ background: C.card, border: `1px solid ${C.border}`, boxShadow: '0 12px 32px rgba(16,19,43,0.14)' }}
        >
          {loading && (
            <p className="px-4 py-3 text-[12px]" style={{ color: C.textMuted }}>Loading platform index…</p>
          )}
          {!loading && !results.length && (
            <p className="px-4 py-3 text-[12px]" style={{ color: C.textMuted }}>No matches for “{query}”</p>
          )}
          {results.map((r, i) => {
            const Icon = r.icon;
            return (
              <button
                key={`${r.kind}-${i}`}
                type="button"
                onClick={() => { navigate(r.to); setOpen(false); setQuery(''); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50"
                style={{ borderTop: i ? `1px solid ${C.border}` : 'none' }}
              >
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: C.textFaint }} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium truncate" style={{ color: C.ink }}>{r.label}</span>
                  {r.sub && (
                    <Mono className="block text-[11px] truncate" style={{ color: C.textMuted }}>{r.sub}</Mono>
                  )}
                </span>
                <span className="text-[10px] uppercase tracking-wider" style={{ color: C.textFaint }}>{r.kind}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SuperAdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const isStaff = user?.role === 'platform_staff';
  const platformPermissions = user?.platformPermissions;

  const groups = useMemo(() => {
    const perms = platformPermissions || {};
    return (
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((it) => {
          if (!isStaff) return true;
          if (it.always) return true;
          if (it.superadminOnly) return false;
          return perms[it.perm]?.read === true;
        }),
      })).filter((g) => g.items.length)
    );
  }, [isStaff, platformPermissions]);

  const isActive = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const sidebarWidth = collapsed ? 76 : 224;

  const sidebar = (
    <aside
      className="fixed lg:static inset-y-0 left-0 z-50 h-screen flex flex-col transition-all duration-300"
      style={{
        width: sidebarWidth,
        background: `linear-gradient(180deg, ${C.sidebarTop} 0%, ${C.sidebarBottom} 100%)`,
        transform: mobileOpen ? 'translateX(0)' : undefined,
      }}
    >
      {/* Brand */}
      <div className={`h-16 flex items-center flex-shrink-0 ${collapsed ? 'justify-center' : 'px-4 gap-2.5'}`}>
        <Link to={BASE} className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(76,111,255,0.18)' }}
          >
            <ShieldCheck className="w-[18px] h-[18px]" style={{ color: C.signal }} />
          </span>
          {!collapsed && (
            <span className="text-[15px] font-semibold text-white" style={{ fontFamily: FONT_UI }}>
              DigiHR
            </span>
          )}
        </Link>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden ml-auto p-2 rounded-lg"
          style={{ color: '#A8AEC9' }}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto pb-4 space-y-4">
        {groups.map((group) => (
          <div key={group.label}>
            {!collapsed ? (
              <div className="flex items-center gap-1.5 px-5 mb-1.5">
                <span className="w-1 h-1 rounded-full" style={{ background: group.dot }} />
                <span
                  className="text-[9px] font-semibold uppercase tracking-[0.12em]"
                  style={{ color: '#7A82A8' }}
                >
                  {group.label}
                </span>
              </div>
            ) : (
              <div className="flex justify-center mb-1.5">
                <span className="w-1 h-1 rounded-full" style={{ background: group.dot }} />
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={isActive(item)}
                  collapsed={collapsed}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse */}
      <div className="p-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center justify-center gap-2 py-2 rounded-lg text-[12px] transition-colors hover:bg-white/5"
          style={{ color: '#A8AEC9' }}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : (<><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>)}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: C.pageBg, fontFamily: FONT_UI }}>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className={`${mobileOpen ? 'block' : 'hidden'} lg:block`}>{sidebar}</div>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Header */}
        <header
          className="h-16 flex items-center justify-between gap-4 px-4 lg:px-6 flex-shrink-0 z-30"
          style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" style={{ color: C.ink }} />
            </button>
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-4">
            <Link to={`${BASE}/announcements`} className="relative p-2 rounded-lg hover:bg-slate-100" title="Announcements">
              <Bell className="w-[18px] h-[18px]" style={{ color: C.textMuted }} />
              <span
                className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                style={{ background: C.amber }}
              />
            </Link>

            <div className="relative" ref={menuRef} style={{ borderLeft: `1px solid ${C.border}` }}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-3 pl-4 py-1 rounded-lg"
              >
                <span className="text-right hidden sm:block">
                  <span className="block text-[13px] font-semibold leading-tight" style={{ color: C.ink }}>
                    {user?.name || 'Super Administrator'}
                  </span>
                  <span className="block text-[11px] leading-tight" style={{ color: C.violet }}>
                    {isStaff ? 'Platform Staff' : 'Superadmin'}
                  </span>
                </span>
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[13px] font-semibold flex-shrink-0"
                  style={{ background: C.signal }}
                >
                  {(user?.name || 'S').charAt(0).toUpperCase()}
                </span>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-52 rounded-xl overflow-hidden z-50"
                  style={{ background: C.card, border: `1px solid ${C.border}`, boxShadow: '0 12px 32px rgba(16,19,43,0.14)' }}
                >
                  <Link
                    to={`${BASE}/change-password`}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-3 text-[13px] hover:bg-slate-50"
                    style={{ color: C.ink }}
                  >
                    <KeyRound className="w-4 h-4" style={{ color: C.textMuted }} />
                    Change password
                  </Link>
                  <button
                    onClick={() => { logout(); navigate('/login'); }}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-[13px] hover:bg-rose-50"
                    style={{ color: C.rose, borderTop: `1px solid ${C.border}` }}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-7" style={{ background: C.pageBg }}>
          <div className="max-w-[1500px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
export { NAV_GROUPS };
